import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/api/client";
import { Link } from "react-router-dom";
import { Building2, Map, Newspaper, MessageSquareQuote, HelpCircle, Inbox, Mail, ArrowUpRight, TrendingUp, MapPin } from "lucide-react";

const typeLabels = {
  contacto: "Contacto",
  comprador: "Compradores",
  vendedor: "Vendedores",
  avaliacao: "Avaliações",
  chat: "Chat",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [counts, setCounts] = useState({ properties: 0, zones: 0, blog: 0, testimonials: 0, faqs: 0, leads: 0, newsletter: 0 });

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    Promise.all([
      api.get("/admin/properties"),
      api.get("/admin/zones"),
      api.get("/admin/blog"),
      api.get("/admin/testimonials"),
      api.get("/admin/faqs"),
      api.get("/admin/leads"),
      api.get("/admin/newsletter"),
    ]).then(([p, z, b, t, f, l, n]) => {
      setCounts({ properties: p.data.length, zones: z.data.length, blog: b.data.length, testimonials: t.data.length, faqs: f.data.length, leads: l.data.length, newsletter: n.data.length });
    }).catch(() => {});
  }, []);

  const maxTrend = stats ? Math.max(1, ...stats.leads.weekly_trend.map((w) => w.count)) : 1;
  const topZone = stats?.leads?.top_zones?.[0];

  const cards = [
    { label: "Imóveis", value: counts.properties, icon: Building2, to: "/admin/imoveis" },
    { label: "Zonas", value: counts.zones, icon: Map, to: "/admin/zonas" },
    { label: "Blog", value: counts.blog, icon: Newspaper, to: "/admin/blog" },
    { label: "Testemunhos", value: counts.testimonials, icon: MessageSquareQuote, to: "/admin/testemunhos" },
    { label: "FAQs", value: counts.faqs, icon: HelpCircle, to: "/admin/faqs" },
    { label: "Newsletter", value: counts.newsletter, icon: Mail, to: "/admin/newsletter" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-stone-900">Bem-vinda, Zélia.</h1>
            <p className="mt-2 text-stone-600">O momento do casa no porto num piscar de olhos.</p>
          </div>
          <Link to="/" className="btn-secondary" data-testid="admin-view-site">Ver site</Link>
        </div>

        {/* Momentum */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="card-soft p-6" data-testid="stat-leads-week">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-stone-500">Últimos 7 dias</div>
              <TrendingUp className="h-4 w-4 text-[#103C4A]" />
            </div>
            <div className="mt-4 font-display text-4xl text-stone-900">{stats?.leads.week ?? "—"}</div>
            <div className="mt-1 text-sm text-stone-600">novos contactos</div>
          </div>
          <div className="card-soft p-6" data-testid="stat-leads-month">
            <div className="text-xs uppercase tracking-widest text-stone-500">Últimos 30 dias</div>
            <div className="mt-4 font-display text-4xl text-stone-900">{stats?.leads.month ?? "—"}</div>
            <div className="mt-1 text-sm text-stone-600">contactos totais</div>
          </div>
          <div className="card-soft p-6" data-testid="stat-leads-new">
            <div className="text-xs uppercase tracking-widest text-stone-500">Ainda por tratar</div>
            <div className="mt-4 font-display text-4xl text-[#103C4A]">{stats?.leads.new ?? "—"}</div>
            <Link to="/admin/leads" className="mt-1 inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#103C4A]">
              Abrir caixa <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="card-soft p-6" data-testid="stat-top-zone">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-stone-500">Zona em destaque</div>
              <MapPin className="h-4 w-4 text-[#103C4A]" />
            </div>
            <div className="mt-4 font-display text-3xl text-stone-900">{topZone?.zone || "—"}</div>
            <div className="mt-1 text-sm text-stone-600">{topZone ? `${topZone.count} contactos no mês` : "Sem dados suficientes"}</div>
          </div>
        </div>

        {/* Trend chart */}
        {stats && (
          <div className="mt-8 card-soft p-6" data-testid="stat-trend">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">Contactos por semana</div>
                <div className="mt-1 font-display text-2xl text-stone-900">Últimas 8 semanas</div>
              </div>
            </div>
            <div className="flex items-end justify-between gap-2 h-40">
              {stats.leads.weekly_trend.map((w, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                  <div className="w-full flex flex-col items-center">
                    <div className="text-xs text-stone-500 mb-1">{w.count}</div>
                    <div
                      className="w-full max-w-[36px] rounded-t-md bg-[#103C4A] transition-all"
                      style={{ height: `${(w.count / maxTrend) * 100}%`, minHeight: w.count > 0 ? "6px" : "2px", opacity: w.count > 0 ? 1 : 0.15 }}
                    />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500">{w.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* By type + Top zones */}
        {stats && (
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <div className="card-soft p-6" data-testid="stat-by-type">
              <div className="text-xs uppercase tracking-widest text-stone-500">Origem dos contactos (30 dias)</div>
              <div className="mt-4 space-y-3">
                {stats.leads.by_type.length === 0 ? (
                  <div className="text-sm text-stone-500">Sem dados ainda.</div>
                ) : stats.leads.by_type.map((t) => {
                  const total = stats.leads.by_type.reduce((s, x) => s + x.count, 0);
                  const pct = Math.round((t.count / total) * 100);
                  return (
                    <div key={t.type}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-800">{typeLabels[t.type] || t.type}</span>
                        <span className="text-stone-500">{t.count} · {pct}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full bg-[#103C4A]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="card-soft p-6" data-testid="stat-top-zones">
              <div className="text-xs uppercase tracking-widest text-stone-500">Zonas mais procuradas (30 dias)</div>
              <div className="mt-4 space-y-3">
                {stats.leads.top_zones.length === 0 ? (
                  <div className="text-sm text-stone-500">Sem dados de zona ainda. As zonas aparecem quando as pessoas preenchem o campo &ldquo;zona&rdquo;.</div>
                ) : stats.leads.top_zones.map((z) => {
                  const max = Math.max(...stats.leads.top_zones.map((x) => x.count));
                  const pct = Math.round((z.count / max) * 100);
                  return (
                    <div key={z.zone}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-800">{z.zone}</span>
                        <span className="text-stone-500">{z.count}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                        <div className="h-full bg-[#103C4A]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Content shortcut cards */}
        <h2 className="mt-12 font-display text-xl text-stone-900">Gerir conteúdo</h2>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c) => (
            <Link key={c.label} to={c.to} data-testid={`admin-stat-${c.label.toLowerCase()}`} className="card-soft p-6 group">
              <div className="flex items-center justify-between">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#103C4A]/10 text-[#103C4A]"><c.icon className="h-5 w-5" /></div>
                <ArrowUpRight className="h-4 w-4 text-stone-400 group-hover:text-[#103C4A]" />
              </div>
              <div className="mt-6 font-display text-4xl text-stone-900">{c.value}</div>
              <div className="mt-1 text-sm text-stone-600">{c.label}</div>
            </Link>
          ))}
          <Link to="/admin/leads" data-testid="admin-stat-leads" className="card-soft p-6 group bg-[#103C4A] text-white border-[#103C4A]">
            <div className="flex items-center justify-between">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Inbox className="h-5 w-5" /></div>
              <ArrowUpRight className="h-4 w-4 opacity-70 group-hover:opacity-100" />
            </div>
            <div className="mt-6 font-display text-4xl">{counts.leads}</div>
            <div className="mt-1 text-sm opacity-80">Contactos</div>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
